import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError, inr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, MagnifyingGlass, UserCircle, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import DocumentsTab from "@/components/DocumentsTab";
import CommunicationActions from "@/components/CommunicationActions";
import TransferDialog from "@/components/TransferDialog";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  name: "", father_name: "", mother_name: "", mobile: "", whatsapp: "", email: "",
  aadhaar: "", pan: "", voter_id: "", passport_no: "",
  dob: "", gender: "", marital_status: "", occupation: "", annual_income: "",
  caste_category: "", religion: "", nationality: "Indian",
  house_no: "", street: "", address: "", city: "", taluka: "", district: "",
  state: "Maharashtra", pin: "", photo: "",
};

export default function Customers() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [transferOpen, setTransferOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileTab, setProfileTab] = useState("overview");
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async (query = "") => {
    const { data } = await api.get("/customers", { params: query ? { q: query } : {} });
    setRows(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Deep link ?id=xxx or ?id=xxx&tab=documents (from global search)
  useEffect(() => {
    const id = params.get("id");
    const tab = params.get("tab");
    if (id) { setDetailId(id); if (tab) setProfileTab(tab); }
  }, [params]);

  useEffect(() => {
    if (!detailId) { setProfile(null); setEditMode(false); return; }
    api.get(`/customers/${detailId}/profile`).then((r) => setProfile(r.data)).catch(() => setProfile(null));
  }, [detailId]);

  const startEdit = () => {
    if (!profile) return;
    const c = profile.customer;
    setEditForm({
      name: c.name || "", father_name: c.father_name || "", mother_name: c.mother_name || "",
      mobile: c.mobile || "", whatsapp: c.whatsapp || "", email: c.email || "",
      aadhaar: c.aadhaar || "", pan: c.pan || "", voter_id: c.voter_id || "", passport_no: c.passport_no || "",
      dob: c.dob || "", gender: c.gender || "", marital_status: c.marital_status || "",
      occupation: c.occupation || "", annual_income: c.annual_income || "",
      caste_category: c.caste_category || "", religion: c.religion || "", nationality: c.nationality || "",
      house_no: c.house_no || "", street: c.street || "", address: c.address || "",
      city: c.city || "", district: c.district || "", state: c.state || "", pin: c.pin || "",
    });
    setEditMode(true);
  };

  const saveEdit = async () => {
    if (!detailId) return;
    if (!editForm.name || !editForm.mobile) { toast.error("Name and mobile are required"); return; }
    setSavingEdit(true);
    try {
      await api.patch(`/customers/${detailId}`, editForm);
      toast.success("Customer updated");
      const { data } = await api.get(`/customers/${detailId}/profile`);
      setProfile(data);
      setEditMode(false);
      load(q);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSavingEdit(false); }
  };

  const onPhoto = (file) => {
    if (!file) return;
    if (file.size > 2_000_000) { toast.error("Photo too large (max 2 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, photo: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name || !form.mobile) { toast.error("Name and mobile are required"); return; }
    setSaving(true);
    try {
      await api.post("/customers", form);
      toast.success("Customer registered");
      setOpen(false); setForm(EMPTY); load(q);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const closeDetail = () => { setDetailId(null); setProfile(null); if (params.get("id")) setParams({}); };

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2">Directory</div>
          <h1 className="text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Customers</h1>
          <p className="text-sm text-slate-600 mt-2">Register walk-ins with complete KYC and manage every profile.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-customer-btn" className="bg-orange-600 hover:bg-orange-700 text-white h-11 gap-2 hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow,background-color] duration-200">
              <Plus size={18} weight="bold" /> New Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Register Customer</DialogTitle>
            </DialogHeader>

            <SectionHeading>Personal Information</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full Name *"><Input data-testid="cust-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="Father / Husband Name"><Input value={form.father_name} onChange={(e) => setForm({ ...form, father_name: e.target.value })} /></Field>
              <Field label="Mother's Name"><Input value={form.mother_name} onChange={(e) => setForm({ ...form, mother_name: e.target.value })} /></Field>
              <Field label="Date of Birth"><Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} /></Field>
              <Field label="Gender">
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Marital Status">
                <Select value={form.marital_status} onValueChange={(v) => setForm({ ...form, marital_status: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Single">Single</SelectItem>
                    <SelectItem value="Married">Married</SelectItem>
                    <SelectItem value="Divorced">Divorced</SelectItem>
                    <SelectItem value="Widowed">Widowed</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Occupation"><Input value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} /></Field>
              <Field label="Annual Income (₹)"><Input value={form.annual_income} onChange={(e) => setForm({ ...form, annual_income: e.target.value })} /></Field>
              <Field label="Caste Category">
                <Select value={form.caste_category} onValueChange={(v) => setForm({ ...form, caste_category: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {["General", "OBC", "SC", "ST", "EWS", "SBC", "VJNT"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Religion"><Input value={form.religion} onChange={(e) => setForm({ ...form, religion: e.target.value })} /></Field>
              <Field label="Nationality"><Input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} /></Field>
            </div>

            <SectionHeading>Contact & Identity</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Mobile *"><Input data-testid="cust-mobile-input" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></Field>
              <Field label="WhatsApp"><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} /></Field>
              <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label="Aadhaar Number"><Input value={form.aadhaar} onChange={(e) => setForm({ ...form, aadhaar: e.target.value })} /></Field>
              <Field label="PAN Number"><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} /></Field>
              <Field label="Voter ID"><Input value={form.voter_id} onChange={(e) => setForm({ ...form, voter_id: e.target.value })} /></Field>
              <Field label="Passport Number"><Input value={form.passport_no} onChange={(e) => setForm({ ...form, passport_no: e.target.value })} /></Field>
            </div>

            <SectionHeading>Address</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="House / Flat No."><Input value={form.house_no} onChange={(e) => setForm({ ...form, house_no: e.target.value })} /></Field>
              <Field label="Street / Area"><Input value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} /></Field>
              <Field label="Village / City"><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
              <Field label="Taluka"><Input value={form.taluka} onChange={(e) => setForm({ ...form, taluka: e.target.value })} /></Field>
              <Field label="District"><Input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} /></Field>
              <Field label="State"><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></Field>
              <Field label="PIN Code"><Input value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} /></Field>
              <div className="md:col-span-2"><Field label="Landmark / Additional Address"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field></div>
            </div>

            <SectionHeading>Photo</SectionHeading>
            <div>
              <input type="file" accept="image/*" data-testid="cust-photo-input"
                onChange={(e) => onPhoto(e.target.files?.[0])}
                className="text-sm file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-slate-900 file:text-white file:cursor-pointer" />
              {form.photo && <img src={form.photo} alt="preview" className="mt-3 w-24 h-24 rounded-md object-cover border border-slate-200" />}
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => { setOpen(false); setForm(EMPTY); }}>Cancel</Button>
              <Button data-testid="cust-save-btn" disabled={saving} onClick={save} className="bg-orange-600 hover:bg-orange-700 text-white">
                {saving ? "Saving…" : "Register"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 bg-white border-slate-200 mb-4">
        <div className="relative max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="cust-search-input"
            placeholder="Search by name, mobile, ID, Aadhaar, PAN, Voter ID, App / Portal No…"
            value={q}
            onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
            className="pl-9 h-10 focus-visible:ring-orange-600/30 focus-visible:border-orange-600"
          />
        </div>
      </Card>

      <Card className="bg-white border-slate-200 overflow-hidden" data-testid="customer-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">ID</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Customer</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Mobile</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Location</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Category</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-16">No customers yet. Register your first walk-in.</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => { setDetailId(r.id); setProfileTab("overview"); }} data-testid={`cust-row-${r.id}`}>
                <TableCell className="font-mono text-xs text-slate-600 group-hover:text-orange-600">{r.customer_no}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {r.photo ? (
                      <img src={r.photo} alt="" className="w-9 h-9 rounded-full object-cover border border-slate-200" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><UserCircle size={22} /></div>
                    )}
                    <div>
                      <div className="font-medium text-slate-900 hover:text-orange-600 hover:underline underline-offset-4">{r.name}</div>
                      <div className="text-xs text-slate-500">{r.father_name || "—"}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-sm">{r.mobile}</TableCell>
                <TableCell className="text-sm text-slate-600">{[r.taluka, r.district, r.state].filter(Boolean).join(", ") || [r.city, r.state].filter(Boolean).join(", ") || "—"}</TableCell>
                <TableCell>{r.caste_category ? <Badge variant="outline" className="text-xs">{r.caste_category}</Badge> : <span className="text-xs text-slate-400">—</span>}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="outline" data-testid={`cust-view-${r.id}`} onClick={() => { setDetailId(r.id); setProfileTab("overview"); }}>Profile</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!detailId} onOpenChange={(v) => !v && closeDetail()}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Customer Profile</DialogTitle></DialogHeader>
          {!profile ? (
            <div className="py-10 text-center text-slate-500">Loading profile…</div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                {profile.customer.photo ? (
                  <img src={profile.customer.photo} className="w-20 h-20 rounded-full object-cover border" alt="" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center"><UserCircle size={40} className="text-slate-400" /></div>
                )}
                <div className="flex-1">
                  <div className="font-mono text-xs text-slate-500">{profile.customer.customer_no}</div>
                  <div className="text-xl font-semibold" style={{ fontFamily: "var(--font-heading)" }}>{profile.customer.name}</div>
                  <div className="text-sm text-slate-600">{profile.customer.mobile} · {profile.customer.email || "—"}</div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <MiniStat label="Docs" value={profile.document_count} />
                  <MiniStat label="Apps" value={profile.applications.length} />
                  <MiniStat label="Paid" value={inr(profile.paid_total)} />
                </div>
                <div className="flex flex-col gap-2">
                  {!editMode ? (
                    <Button size="sm" variant="outline" onClick={startEdit} data-testid="cust-edit-btn" className="gap-1.5">
                      <PencilSimple size={14} weight="bold" /> Edit
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setEditMode(false)} data-testid="cust-edit-cancel-btn">Cancel</Button>
                  )}
                  {isSuper && !editMode && (
                    <Button size="sm" variant="outline" onClick={() => setTransferOpen(true)} data-testid="cust-transfer-btn" className="gap-1.5 text-orange-700 border-orange-300 hover:bg-orange-50">
                      Transfer branch
                    </Button>
                  )}
                </div>
              </div>

              <div className="border-t border-b border-slate-200 py-3 -mx-6 px-6">
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Communication Center</div>
                <CommunicationActions customer={profile.customer} />
              </div>

              <Tabs value={profileTab} onValueChange={setProfileTab}>
                <TabsList className="bg-slate-100 flex-wrap h-auto">
                  <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                  <TabsTrigger value="documents" data-testid="tab-documents">Documents ({profile.document_count})</TabsTrigger>
                  <TabsTrigger value="applications" data-testid="tab-applications">Applications ({profile.applications.length})</TabsTrigger>
                  <TabsTrigger value="complaints" data-testid="tab-complaints">Complaints ({profile.complaints.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="pt-4">
                  {editMode ? (
                    <div className="space-y-4">
                      <SectionHeading>Personal</SectionHeading>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Full Name *"><Input data-testid="cust-edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></Field>
                        <Field label="Father/Husband"><Input value={editForm.father_name} onChange={(e) => setEditForm({ ...editForm, father_name: e.target.value })} /></Field>
                        <Field label="Mother's Name"><Input value={editForm.mother_name} onChange={(e) => setEditForm({ ...editForm, mother_name: e.target.value })} /></Field>
                        <Field label="DOB"><Input type="date" value={editForm.dob} onChange={(e) => setEditForm({ ...editForm, dob: e.target.value })} /></Field>
                        <Field label="Gender"><Input value={editForm.gender} onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })} /></Field>
                        <Field label="Occupation"><Input value={editForm.occupation} onChange={(e) => setEditForm({ ...editForm, occupation: e.target.value })} /></Field>
                      </div>
                      <SectionHeading>Contact & Identity</SectionHeading>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Mobile *"><Input data-testid="cust-edit-mobile" value={editForm.mobile} onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })} /></Field>
                        <Field label="WhatsApp"><Input value={editForm.whatsapp} onChange={(e) => setEditForm({ ...editForm, whatsapp: e.target.value })} /></Field>
                        <Field label="Email"><Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></Field>
                        <Field label="Aadhaar"><Input value={editForm.aadhaar} onChange={(e) => setEditForm({ ...editForm, aadhaar: e.target.value })} /></Field>
                        <Field label="PAN"><Input value={editForm.pan} onChange={(e) => setEditForm({ ...editForm, pan: e.target.value })} /></Field>
                        <Field label="Voter ID"><Input value={editForm.voter_id} onChange={(e) => setEditForm({ ...editForm, voter_id: e.target.value })} /></Field>
                      </div>
                      <SectionHeading>Address</SectionHeading>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="House / Flat"><Input value={editForm.house_no} onChange={(e) => setEditForm({ ...editForm, house_no: e.target.value })} /></Field>
                        <Field label="Street"><Input value={editForm.street} onChange={(e) => setEditForm({ ...editForm, street: e.target.value })} /></Field>
                        <Field label="City / Village"><Input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} /></Field>
                        <Field label="District"><Input value={editForm.district} onChange={(e) => setEditForm({ ...editForm, district: e.target.value })} /></Field>
                        <Field label="State"><Input value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} /></Field>
                        <Field label="PIN"><Input value={editForm.pin} onChange={(e) => setEditForm({ ...editForm, pin: e.target.value })} /></Field>
                      </div>
                      <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                        <Button variant="outline" onClick={() => setEditMode(false)} disabled={savingEdit}>Cancel</Button>
                        <Button data-testid="cust-edit-save-btn" onClick={saveEdit} disabled={savingEdit} className="bg-orange-600 hover:bg-orange-700 text-white">
                          {savingEdit ? "Saving…" : "Save Changes"}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <Info label="Father/Husband" value={profile.customer.father_name} />
                      <Info label="Mother" value={profile.customer.mother_name} />
                      <Info label="Gender" value={profile.customer.gender} />
                      <Info label="DOB" value={profile.customer.dob} />
                      <Info label="Marital Status" value={profile.customer.marital_status} />
                      <Info label="Occupation" value={profile.customer.occupation} />
                      <Info label="Annual Income" value={profile.customer.annual_income} />
                      <Info label="Caste Category" value={profile.customer.caste_category} />
                      <Info label="Religion" value={profile.customer.religion} />
                      <Info label="Nationality" value={profile.customer.nationality} />
                      <Info label="Aadhaar" value={profile.customer.aadhaar} mono />
                      <Info label="PAN" value={profile.customer.pan} mono />
                      <Info label="Voter ID" value={profile.customer.voter_id} mono />
                      <Info label="Passport" value={profile.customer.passport_no} mono />
                      <div className="col-span-2"><Info label="Address" value={[profile.customer.house_no, profile.customer.street, profile.customer.city, profile.customer.taluka, profile.customer.district, profile.customer.state, profile.customer.pin].filter(Boolean).join(", ")} /></div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="documents" className="pt-4">
                  <DocumentsTab customerId={profile.customer.id} />
                </TabsContent>

                <TabsContent value="applications" className="pt-4">
                  {profile.applications.length === 0 ? (
                    <div className="text-sm text-slate-500 py-8 text-center">No applications yet.</div>
                  ) : (
                    <div className="space-y-2">
                      {profile.applications.map((a) => (
                        <Card key={a.id} className="p-3 bg-white border-slate-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-sm">{a.service_name}</div>
                              <div className="text-xs text-slate-500 font-mono">{a.app_no}{a.portal_app_no ? ` · Portal ${a.portal_app_no}` : ""}</div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold">{inr(a.total)}</div>
                              <div className="text-xs"><Badge variant="outline" className="mr-1">{a.status}</Badge>{a.payment_status === "Paid" ? <Badge className="bg-emerald-600 text-white">Paid</Badge> : <Badge variant="outline" className="border-amber-300 text-amber-700">Unpaid</Badge>}</div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="complaints" className="pt-4">
                  {profile.complaints.length === 0 ? (
                    <div className="text-sm text-slate-500 py-8 text-center">No complaints filed.</div>
                  ) : (
                    <div className="space-y-2">
                      {profile.complaints.map((c) => (
                        <Card key={c.id} className="p-3 bg-white border-slate-200">
                          <div className="flex items-center justify-between">
                            <div><div className="font-medium text-sm">{c.category}</div><div className="text-xs text-slate-500 font-mono">{c.complaint_no}</div></div>
                            <div><Badge variant="outline">{c.status}</Badge></div>
                          </div>
                          <div className="text-xs text-slate-600 mt-2">{c.description}</div>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <TransferDialog
        open={transferOpen}
        onOpenChange={setTransferOpen}
        entity="customer"
        entityId={profile?.customer?.id}
        entityLabel={profile ? `${profile.customer.customer_no} · ${profile.customer.name} · ${profile.customer.mobile}` : ""}
        fromBranchId={profile?.customer?.branch_id}
        onTransferred={() => {
          if (detailId) api.get(`/customers/${detailId}/profile`).then((r) => setProfile(r.data)).catch(() => {});
          load();
        }}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
function SectionHeading({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-orange-600 mt-6 mb-3 pb-1 border-b border-slate-200">{children}</div>;
}
function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
      <div className={`text-sm text-slate-900 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-lg font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</div>
    </div>
  );
}
